import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';

/**
 * @typedef {Object} DashboardStoreState
 * @property {string | null} dashboardUserAuthId - The auth_id of the user whose dashboard is currently open
 * @property {import('@/models/OrgCompassDevice/OrgCompassDeviceListItem').DashboardDeviceOverride | null} dashboardDeviceOverride - Device snapshot to prefer over the live ping when opened from the org device list
 * @property {boolean} isDashboardOpen - Whether the dashboard is currently open
 */

/**
 * @typedef {Object} DashboardStoreActions
 * @property {(authId: string | null, deviceOverride?: import('@/models/OrgCompassDevice/OrgCompassDeviceListItem').DashboardDeviceOverride | null) => void} openDashboard - Opens the dashboard for the specified user auth_id, optionally pinning a device override (device-only view when authId is null but an override is present)
 * @property {() => void} closeDashboard - Closes the dashboard and clears any device override
 */

/** @type {import('zustand').UseBoundStore<import('zustand').StoreApi<DashboardStoreState & DashboardStoreActions>>} */
export const useDashboardStore = create(
  subscribeWithSelector((set) => ({
    // State
    dashboardUserAuthId: null,
    dashboardDeviceOverride: null,
    isDashboardOpen: false,

    // Actions
    openDashboard: (authId, deviceOverride = null) =>
      set({
        dashboardUserAuthId: authId,
        dashboardDeviceOverride: deviceOverride,
        isDashboardOpen: authId !== null || deviceOverride !== null,
      }),
    closeDashboard: () =>
      set({
        dashboardUserAuthId: null,
        dashboardDeviceOverride: null,
        isDashboardOpen: false,
      }),
  })),
);

// Selector hooks for efficient re-renders
export const useDashboardUserAuthId = () =>
  useDashboardStore((state) => state.dashboardUserAuthId);
export const useDashboardDeviceOverride = () =>
  useDashboardStore((state) => state.dashboardDeviceOverride);
export const useIsDashboardOpen = () =>
  useDashboardStore((state) => state.isDashboardOpen);

// Action hooks
export const useDashboardActions = () => ({
  openDashboard: useDashboardStore.getState().openDashboard,
  closeDashboard: useDashboardStore.getState().closeDashboard,
});
